import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import { useEscapeKey } from "../lib/useEscapeKey";
import {
  CURRENT_WHATS_NEW,
  WHATS_NEW_RELEASES,
  getSeenWhatsNewReleaseId,
  markWhatsNewReleaseSeen,
} from "../lib/whatsNew";
import type { WhatsNewRelease, WhatsNewSection } from "../lib/whatsNew";

const SECTION_STYLES: Record<
  WhatsNewSection["kind"],
  { badge: string; dot: string }
> = {
  new: {
    badge: "border-emerald-200 bg-emerald-50 text-emerald-800",
    dot: "bg-emerald-500",
  },
  improved: {
    badge: "border-sky-200 bg-sky-50 text-sky-800",
    dot: "bg-sky-500",
  },
  fixed: {
    badge: "border-amber-200 bg-amber-50 text-amber-800",
    dot: "bg-amber-500",
  },
};

export default function WhatsNewLauncher({
  blocked = false,
  hideTrigger = false,
  onOpenChange,
  openSignal = 0,
  suppressAutomaticOpen = false,
}: {
  blocked?: boolean;
  /** Geöffnet wird dann über das Konto-Menü (#630). */
  hideTrigger?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** Jede Erhöhung öffnet den Dialog. */
  openSignal?: number;
  suppressAutomaticOpen?: boolean;
}) {
  const [seenReleaseId, setSeenReleaseId] = useState<
    string | null | undefined
  >(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const automaticOpenCheckedRef = useRef(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (automaticOpenCheckedRef.current) return;
    automaticOpenCheckedRef.current = true;
    const storedReleaseId = getSeenWhatsNewReleaseId();
    setSeenReleaseId(storedReleaseId);
    if (
      !blocked &&
      !suppressAutomaticOpen &&
      storedReleaseId !== CURRENT_WHATS_NEW.id
    ) {
      setIsOpen(true);
    }
  }, [blocked, onOpenChange, suppressAutomaticOpen]);

  useEffect(() => {
    if (!blocked || !isOpen) return;
    setIsOpen(false);
  }, [blocked, isOpen, onOpenChange]);

  useEffect(() => {
    onOpenChange?.(isOpen && !blocked);
  }, [blocked, isOpen, onOpenChange]);

  useEffect(() => {
    if (openSignal > 0 && !blocked) setIsOpen(true);
  }, [openSignal, blocked]);

  const hasUnread =
    seenReleaseId !== undefined && seenReleaseId !== CURRENT_WHATS_NEW.id;

  function closeDialog() {
    markWhatsNewReleaseSeen(CURRENT_WHATS_NEW.id);
    setSeenReleaseId(CURRENT_WHATS_NEW.id);
    setIsOpen(false);
    window.requestAnimationFrame(() => (triggerRef.current ?? document.querySelector<HTMLElement>("[data-account-menu-button]"))?.focus());
  }

  function openDialog() {
    if (blocked) return;
    setIsOpen(true);
  }

  return (
    <>
      {hideTrigger ? null : <button
        ref={triggerRef}
        type="button"
        onClick={openDialog}
        disabled={blocked}
        className="relative inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-300"
        aria-label={
          hasUnread ? "Neuerungen anzeigen, ungelesen" : "Neuerungen anzeigen"
        }
        title="Neuerungen"
      >
        <SparkleIcon />
        {hasUnread ? (
          <span
            aria-hidden="true"
            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white"
          />
        ) : null}
      </button>}
      {isOpen && !blocked ? (
        <WhatsNewDialog releases={WHATS_NEW_RELEASES} onClose={closeDialog} />
      ) : null}
    </>
  );
}

function WhatsNewDialog({
  releases,
  onClose,
}: {
  releases: readonly WhatsNewRelease[];
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const scrollRegionRef = useRef<HTMLDivElement | null>(null);
  const [expandedReleaseIds, setExpandedReleaseIds] = useState<Set<string>>(
    () => new Set(),
  );
  useEscapeKey(onClose, true);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (scrollRegionRef.current) scrollRegionRef.current.scrollTop = 0;
    closeButtonRef.current?.focus();
    function keepFocusInside(event: FocusEvent) {
      const dialog = dialogRef.current;
      if (dialog && !dialog.contains(event.target as Node)) {
        closeButtonRef.current?.focus();
      }
    }
    document.addEventListener("focusin", keepFocusInside);
    return () => {
      document.removeEventListener("focusin", keepFocusInside);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  function toggleRelease(releaseId: string) {
    setExpandedReleaseIds((current) => {
      const next = new Set(current);
      if (next.has(releaseId)) next.delete(releaseId);
      else next.add(releaseId);
      return next;
    });
  }

  const dialog = (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
      aria-describedby="whats-new-description"
      tabIndex={-1}
      onKeyDown={(event) => trapDialogFocus(event, dialogRef)}
      onMouseDown={onClose}
    >
      <div
        className="flex max-h-[calc(100dvh-3rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl sm:max-h-[44rem]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Neuerungen
            </p>
            <h2
              id="whats-new-title"
              className="mt-1 text-lg font-semibold text-slate-950"
            >
              Neu in TruuCost Beta
            </h2>
            <p
              id="whats-new-description"
              className="mt-1 text-sm text-slate-600"
            >
              Aktuelle Änderungen und frühere Beta-Updates.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Neuerungen schließen"
            className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          >
            ×
          </button>
        </header>
        <div
          ref={scrollRegionRef}
          data-testid="whats-new-scroll-region"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5"
        >
          <div className="space-y-4">
            {releases.map((release, index) => (
              <WhatsNewReleaseBlock
                key={release.id}
                release={release}
                latest={index === 0}
                expanded={
                  index === 0 || expandedReleaseIds.has(release.id)
                }
                onToggle={() => toggleRelease(release.id)}
              />
            ))}
          </div>
        </div>
        <footer className="flex flex-none justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center justify-center rounded-md bg-slate-950 px-4 text-sm font-medium text-white hover:bg-slate-800"
          >
            Gelesen
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}

function WhatsNewReleaseBlock({
  release,
  latest,
  expanded,
  onToggle,
}: {
  release: WhatsNewRelease;
  latest: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const headingId = `whats-new-${release.id}-heading`;
  const contentId = `whats-new-${release.id}-content`;

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
    >
      {latest ? (
        <div className="border-b border-slate-200 px-4 py-3.5">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
              Aktuell
            </span>
            <span className="font-semibold text-slate-600">
              {release.versionLabel}
            </span>
            <span aria-hidden="true" className="text-slate-300">
              ·
            </span>
            <time dateTime={release.publishedAt} className="text-slate-500">
              {release.publishedLabel}
            </time>
          </div>
          <h3 id={headingId} className="mt-2 font-semibold text-slate-950">
            {release.title}
          </h3>
        </div>
      ) : (
        <h3 id={headingId}>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={contentId}
            onClick={onToggle}
            className="flex w-full items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500"
          >
            <span className="min-w-0">
              <span className="block text-xs font-semibold text-slate-600">
                {release.versionLabel} · {release.publishedLabel}
              </span>
              <span className="mt-1 block text-sm font-semibold text-slate-900">
                {release.title}
              </span>
            </span>
            <span aria-hidden="true" className="flex-none text-slate-500">
              {expanded ? "▾" : "▸"}
            </span>
          </button>
        </h3>
      )}

      <div id={contentId} hidden={!expanded} className="space-y-4 p-4">
        <p className="text-sm leading-6 text-slate-600">{release.intro}</p>
        {release.sections.map((section) => (
          <WhatsNewSectionBlock key={section.kind} section={section} />
        ))}
      </div>
    </section>
  );
}

function WhatsNewSectionBlock({ section }: { section: WhatsNewSection }) {
  const style = SECTION_STYLES[section.kind];
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h4>
        <span
          className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-semibold ${style.badge}`}
        >
          {section.title}
        </span>
      </h4>
      <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-700">
        {section.items.map((item) => (
          <li key={item} className="flex gap-2">
            <span
              aria-hidden="true"
              className={`mt-2.5 h-1.5 w-1.5 flex-none rounded-full ${style.dot}`}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SparkleIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.5 2.5 11 7l4.5 1.5L11 10l-1.5 4.5L8 10 3.5 8.5 8 7l1.5-4.5Z" />
      <path d="m15 12.5.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6.6-1.9Z" />
    </svg>
  );
}

function trapDialogFocus(
  event: KeyboardEvent<HTMLDivElement>,
  dialogRef: RefObject<HTMLDivElement | null>,
) {
  if (event.key !== "Tab") return;
  const dialog = dialogRef.current;
  if (!dialog) return;
  const focusable = Array.from(
    dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => element.getAttribute("aria-hidden") !== "true");
  if (focusable.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}
